import { useAppState } from "../../context";
import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { UserType, OrderResponseType, CheckoutFormData, CartItemType, OrderType, LeadTimePriceType } from "../../types";
import { useForm } from "react-hook-form";
import { useCart } from "../cart";
import { fetchJson } from "../../utils";
import { PRODUCT_TYPE } from "../../constants";
import SignIn from "../sign-in";
import { loadStripe } from '@stripe/stripe-js';





const formValidation = {
    email: {
        required: 'Email is required',
        pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: 'Invalid email address'
        }
    },
    firstName: { required: 'First name is required' },
    lastName: { required: 'Last name is required' },
    country: { required: 'Country is required' },
    streetAddress: { required: 'Street address is required' },
    city: { required: 'City is required' },
    postcode: { required: 'Postcode is required' },
    phone: {
        required: 'Phone is required',
        pattern: {
            value: /^[0-9+\-\s()]*$/,
            message: 'Invalid phone number'
        }
    },
    paymentMethod: { required: 'Please select a payment method' }
};

export const CheckoutPage = () => {
    const {
        couponValue,
        setCouponValue,
        couponError,
        handleCouponApply,
        cartWithProducts,
        checkoutReadyData
    } = useCart();

    const [isCheckoutReady] = checkoutReadyData;
    const [user] = useAppState<UserType>('user', {} as UserType);
    // const [, setCart] = useAppState<CartItemType[]>('cart', []);
    // const [, setOrders] = useAppState<OrderType[]>('orders', []);
    const [leadTimePrices] = useAppState<LeadTimePriceType[]>('leadTimePrices', []);
    const navigate = useNavigate();
    const [stripePublishableKey] = useAppState<string>('stripePublishableKey', undefined);
    const [loadingStripe, setLoadingStripe] = useState<boolean>(false);

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        setError
    } = useForm<CheckoutFormData>({
        defaultValues: {
            email: user.email ?? '',
            firstName: user.profile?.firstName ?? '',
            lastName: user.profile?.lastName ?? '',
            company: user.profile?.company,
            country: user.profile?.country ?? '',
            streetAddress: user.profile?.streetAddress ?? '',
            apartment: user.profile?.apartment,
            city: user.profile?.city ?? '',
            county: user.profile?.county,
            postcode: user.profile?.postcode ?? '',
            phone: user.profile?.phone ?? '',
            orderNotes: user.profile?.orderNotes,
        }
    });

    useEffect(() => {
        if (!isCheckoutReady) {
            navigate(user ? '/account/cart' : '/cart');
        }
    }, [isCheckoutReady, user]);

    if (!isCheckoutReady) {
        return null;
    }



    const onSubmit = async (formData: CheckoutFormData) => {
        setLoadingStripe(true);
        try {
            // 1. Create order
            const { json } = await fetchJson<OrderResponseType>('/api/order', {
                method: 'POST',
                body: JSON.stringify(formData)
            });

            if (json.success && json.order && json.order.id) {
                // 2. Create Stripe Checkout Session
                const {json: {sessionId}} = await fetchJson<{ sessionId: string }>('/api/checkout/create-session', {
                    method: 'POST',
                    body: JSON.stringify({ orderId: json.order.id })
                });
                if (sessionId && stripePublishableKey) {
                    // 3. Redirect to Stripe Checkout
                    const stripe = await loadStripe(stripePublishableKey);
                    if (stripe) {
                        await stripe.redirectToCheckout({ sessionId });
                        // The user will be redirected away, so no further action needed
                        return;
                    } else {
                        setError('root', {
                            type: 'manual',
                            message: 'Stripe failed to load. Please try again.'
                        });
                    }
                } else {
                    setError('root', {
                        type: 'manual',
                        message: 'Failed to initiate payment session. Please try again.'
                    });
                }
            } else {
                if (json.field) {
                    setError(json.field, {
                        type: 'manual',
                        message: json.error || `Invalid ${json.field}`
                    });
                } else {
                    setError('root', {
                        type: 'manual',
                        message: json.error || 'Failed to create order'
                    });
                }
            }
        } catch (error) {
            setError('root', {
                type: 'manual',
                message: error instanceof Error ? error.message : 'Failed to process order'
            });
        } finally {
            setLoadingStripe(false);
        }
    };

    if (Object.keys(user).length === 0) {
        return <SignIn redirectTo="/checkout" />
    }

    const discount = cartWithProducts.reduce((sum, { cartItem }) =>
        sum + (cartItem.discountAmount || 0), 0);

    const subtotal = cartWithProducts.reduce((sum, { cartItem }) =>
        sum + cartItem.baseCost, 0) - discount;


    const TAX_RATE = 0.08875;
    const salesTax = subtotal * TAX_RATE;
    const total = salesTax + subtotal;
    const hasSimpleProduct = cartWithProducts.some(item => item.product.type === PRODUCT_TYPE.SIMPLE);

    return (
        <>
            <section className="section">
                <div className="inside-wrapper">
                    <ol className="ordersteps">
                        <li className="ordersteps__step m-ok">Upload your photos</li>
                        <li className="ordersteps__step m-ok">Make a payment</li>
                        <li className="ordersteps__step">Download your video</li>
                    </ol>

                    <h1 className="section__title">Checkout</h1>

                    <form onSubmit={handleSubmit(onSubmit)} className={`checkout ${!hasSimpleProduct ? 'checkout__single-col' : ''}`}>
                        {hasSimpleProduct && <div className="checkout__col">
                            <div className="checkout__details form">
                                <h2 className="checkout__title">Billing details</h2>

                                <div className={`form__row ${errors.firstName && 'is-error'}`}>
                                    <label className="form__label">
                                        First name <span className="m-alert">*</span>
                                    </label>
                                    <div className="form__input-holder">
                                        <input
                                            type="text"
                                            className="form__input"
                                            {...register('firstName', formValidation.firstName)}
                                        />
                                    </div>
                                    {errors.firstName && (
                                        <div className="form__alert">{errors.firstName.message}</div>
                                    )}
                                </div>

                                <div className={`form__row ${errors.lastName && 'is-error'}`}>
                                    <label className="form__label">
                                        Last name <span className="m-alert">*</span>
                                    </label>
                                    <div className="form__input-holder">
                                        <input
                                            type="text"
                                            className="form__input"
                                            {...register('lastName', formValidation.lastName)}
                                        />
                                    </div>
                                    {errors.lastName && (
                                        <div className="form__alert">{errors.lastName.message}</div>
                                    )}
                                </div>

                                <div className="form__row">
                                    <label className="form__label">Company name (optional)</label>
                                    <div className="form__input-holder">
                                        <input
                                            type="text"
                                            className="form__input"
                                            {...register('company')}
                                        />
                                    </div>
                                </div>

                                <div className="form__row">
                                    <label className="form__label">
                                        Country / Region <span className="m-alert">*</span>
                                    </label>
                                    <div className="form__input-holder">
                                        <input
                                            type="text"
                                            className="form__input"
                                            defaultValue="USA"
                                            {...register('country', { required: true })}
                                        />
                                    </div>
                                </div>

                                <div className={`form__row ${errors.streetAddress && 'is-error'}`}>
                                    <label className="form__label">
                                        Street address <span className="m-alert">*</span>
                                    </label>
                                    <div className="form__input-holder">
                                        <input
                                            type="text"
                                            className="form__input"
                                            placeholder="House number and street name"
                                            {...register('streetAddress', formValidation.streetAddress)}
                                        />
                                    </div>
                                    {errors.streetAddress && (
                                        <div className="form__alert">{errors.streetAddress.message}</div>
                                    )}
                                </div>

                                <div className="form__row">
                                    <div className="form__input-holder">
                                        <input
                                            type="text"
                                            className="form__input"
                                            placeholder="Apartment, suite, unit, etc. (optional)"
                                            {...register('apartment')}
                                        />
                                    </div>
                                </div>

                                <div className={`form__row ${errors.city && 'is-error'}`}>
                                    <label className="form__label">
                                        Town / City <span className="m-alert">*</span>
                                    </label>
                                    <div className="form__input-holder">
                                        <input
                                            type="text"
                                            className="form__input"
                                            {...register('city', formValidation.city)}
                                        />
                                    </div>
                                    {errors.city && (
                                        <div className="form__alert">{errors.city.message}</div>
                                    )}
                                </div>

                                <div className="form__row">
                                    <label className="form__label">Country (optional)</label>
                                    <div className="form__input-holder">
                                        <input
                                            type="text"
                                            className="form__input"
                                            {...register('country')}
                                        />
                                    </div>
                                </div>

                                <div className={`form__row ${errors.postcode && 'is-error'}`}>
                                    <label className="form__label">
                                        Postcode <span className="m-alert">*</span>
                                    </label>
                                    <div className="form__input-holder">
                                        <input
                                            type="text"
                                            className="form__input"
                                            {...register('postcode', formValidation.postcode)}
                                        />
                                    </div>
                                    {errors.postcode && (
                                        <div className="form__alert">{errors.postcode.message}</div>
                                    )}
                                </div>

                                <div className={`form__row ${errors.phone && 'is-error'}`}>
                                    <label className="form__label">
                                        Phone <span className="m-alert">*</span>
                                    </label>
                                    <div className="form__input-holder">
                                        <input
                                            type="tel"
                                            className="form__input"
                                            {...register('phone', formValidation.phone)}
                                        />
                                    </div>
                                    {errors.phone && (
                                        <div className="form__alert">{errors.phone.message}</div>
                                    )}
                                </div>

                                <div className="form__row">
                                    <label className="form__label">Order notes (optional)</label>
                                    <div className="form__input-holder">
                                        <textarea
                                            className="form__input form__textarea js_textarea"
                                            {...register('orderNotes')}
                                        ></textarea>
                                    </div>
                                </div>
                            </div>
                        </div>}

                        <div className="checkout__col">
                            <div className="checkout__order">
                                <h2 className="checkout__title">Your order</h2>

                                <table className="ordertable">
                                    <tbody>
                                        <tr>
                                            <th>Product</th>
                                            <th>Subtotal</th>
                                        </tr>
                                        {cartWithProducts.map(({ cartItem, product }) => (
                                            <Fragment key={cartItem.id}>
                                                <tr>
                                                    <td>
                                                        <div className="ordertable__product">
                                                            <div className="ordertable__product-name">
                                                                {product.name}
                                                            </div>
                                                            <div className="ordertable__product-quantity">
                                                                {product.type === PRODUCT_TYPE.SIMPLE && `x${cartItem.quantity || 1}`}

                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>${cartItem.baseCost.toFixed(2)}</td>
                                                </tr>
                                                {product.type === PRODUCT_TYPE.CLIP &&
                                                    (cartItem.additionalPhotoCount > 0 || cartItem.leadTimeCost > 0) &&
                                                    <tr className="sub-product__row">
                                                        <td>
                                                            <div className="ordertable__product">
                                                                <div className="ordertable__product-name">
                                                                    Base price
                                                                </div>
                                                                <div className="ordertable__product-quantity">

                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>${(cartItem.productPrice ?? 0).toFixed(2)}</td>
                                                    </tr>}
                                                {product.type === PRODUCT_TYPE.CLIP && cartItem.additionalPhotoCount > 0 &&
                                                    <tr className="sub-product__row">
                                                        <td>
                                                            <div className="ordertable__product">
                                                                <div className="ordertable__product-name">
                                                                    Extra pictures
                                                                </div>
                                                                <div className="ordertable__product-quantity">
                                                                    {`+${cartItem.additionalPhotoCount}`}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>${(cartItem.extraPhotoCost ?? 0).toFixed(2)}</td>
                                                    </tr>}

                                                {product.type === PRODUCT_TYPE.CLIP &&
                                                    (cartItem.leadTimeCost ?? 0) > 0 &&
                                                    cartItem.leadTimePrice &&
                                                    <tr className="sub-product__row">
                                                        <td>
                                                            <div className="ordertable__product">
                                                                <div className="ordertable__product-name">
                                                                    {leadTimePrices.find(price => price.id === cartItem.leadTimePrice)?.caption}
                                                                </div>
                                                                <div className="ordertable__product-quantity">

                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>${(cartItem.leadTimeCost ?? 0).toFixed(2)}</td>
                                                    </tr>}
                                            </Fragment>
                                        )

                                        )}
                                        {discount > 0 && (
                                            <tr>
                                                <td>Coupon code</td>
                                                <td>-${discount.toFixed(2)}</td>
                                            </tr>
                                        )}
                                        <tr>
                                            <th>Subtotal</th>
                                            <td>${subtotal.toFixed(2)}</td>
                                        </tr>

                                        <tr>
                                            <td>Sales tax</td>
                                            <td>${salesTax.toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <th>Total</th>
                                            <td>
                                                <b className="ordertable__total">${total.toFixed(2)}</b>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <div className={`checkout__coupon form`}>
                                    <div className={`checkout__coupon-holder form__input-holder ${couponError ? 'is-error' : ''}`}>
                                        <input type="text" className={`form__input form__input_lg`}
                                            placeholder="Coupon code"
                                            value={couponValue}
                                            onChange={(e) => setCouponValue(e.target.value)}
                                        />
                                        <button className="btn btn_cta btn_lg"
                                            disabled={!couponValue}
                                            type="button" onClick={handleCouponApply}>
                                            APPLY COUPON
                                        </button>
                                        {couponError && <div className="form__alert is-error" style={{
                                            margin: '0',
                                            width: '100%',
                                        }}>{couponError}</div>}
                                    </div>


                                </div>

                                {subtotal > 0 &&
                                    <>
                                        <h2 className="checkout__title">Payment methods</h2>

                                        <div className={`checkout__payments ${errors.paymentMethod ? 'is-error' : ''}`}>
                                            <label className="radiocheck radiocheck_radio">
                                                <input
                                                    type="radio"
                                                    className="radiocheck__input"
                                                    value="card"
                                                    {...register('paymentMethod', formValidation.paymentMethod)}
                                                />
                                                <span className="radiocheck__text">
                                                    <span className="radiocheck__payment">
                                                        <span className="radiocheck__payment-name">
                                                            Credit or Debit Card
                                                        </span>
                                                        <span className="radiocheck__payment-icons">
                                                            <span className="radiocheck__payment-icon">
                                                                <img className="radiocheck__payment-icon-img" src="./img/cards/visa.svg" alt="Visa" title="Visa" />
                                                            </span>
                                                            <span className="radiocheck__payment-icon">
                                                                <img className="radiocheck__payment-icon-img" src="./img/cards/mastercard.svg" alt="mastercard" title="mastercard" />
                                                            </span>
                                                            <span className="radiocheck__payment-icon">
                                                                <img className="radiocheck__payment-icon-img" src="./img/cards/american_express.svg" alt="American Express" title="American Express" />
                                                            </span>
                                                            <span className="radiocheck__payment-icon">
                                                                <img className="radiocheck__payment-icon-img" src="./img/cards/discover.svg" alt="Discover" title="Discover" />
                                                            </span>
                                                        </span>
                                                    </span>
                                                </span>
                                            </label>
                                            <label className="radiocheck radiocheck_radio">
                                                <input
                                                    type="radio"
                                                    className="radiocheck__input"
                                                    value="paypal"
                                                    {...register('paymentMethod', formValidation.paymentMethod)}
                                                />
                                                <span className="radiocheck__text">
                                                    <span className="radiocheck__payment">
                                                        <span className="radiocheck__payment-name">
                                                            PayPal
                                                        </span>
                                                        <span className="radiocheck__payment-icons">
                                                            <span className="radiocheck__payment-icon">
                                                                <img className="radiocheck__payment-icon-img" src="./img/cards/paypal.svg" alt="PayPal" title="PayPal" />
                                                            </span>
                                                        </span>
                                                    </span>
                                                </span>
                                            </label>
                                            <label className="radiocheck radiocheck_radio">
                                                <input
                                                    type="radio"
                                                    className="radiocheck__input"
                                                    value="apple"
                                                    {...register('paymentMethod', formValidation.paymentMethod)}
                                                />
                                                <span className="radiocheck__text">
                                                    <span className="radiocheck__payment">
                                                        <span className="radiocheck__payment-name">
                                                            Apple Pay
                                                        </span>
                                                        <span className="radiocheck__payment-icons">
                                                            <span className="radiocheck__payment-icon">
                                                                <img className="radiocheck__payment-icon-img" src="./img/cards/apple_pay.svg" alt="Apple Pay" title="Apple Pay" />
                                                            </span>
                                                        </span>
                                                    </span>
                                                </span>
                                            </label>
                                            <label className="radiocheck radiocheck_radio">
                                                <input
                                                    type="radio"
                                                    className="radiocheck__input"
                                                    value="google"
                                                    {...register('paymentMethod', formValidation.paymentMethod)}
                                                />
                                                <span className="radiocheck__text">
                                                    <span className="radiocheck__payment">
                                                        <span className="radiocheck__payment-name">
                                                            Google Pay
                                                        </span>
                                                        <span className="radiocheck__payment-icons">
                                                            <span className="radiocheck__payment-icon">
                                                                <img className="radiocheck__payment-icon-img" src="./img/cards/google_pay.svg" alt="Google Pay" title="Google Pay" />
                                                            </span>
                                                        </span>
                                                    </span>
                                                </span>
                                            </label>
                                            {/*<label className="radiocheck radiocheck_radio">
                                                <input
                                                    type="radio"
                                                    className="radiocheck__input"
                                                    value="coupon"
                                                    {...register('paymentMethod', formValidation.paymentMethod)}
                                                />
                                                <span className="radiocheck__text">
                                                    <span className="radiocheck__payment">
                                                        <span className="radiocheck__payment-name">
                                                        Cryptocurrency
                                                        </span>
                                                        <span className="radiocheck__payment-icons">
                                                            {['bitcoin', 'bnb', 'ethereum', 'tether', 'toncoin'].map(crypto => (
                                                                <span className="radiocheck__payment-icon" style={{width: 'auto', minWidth: '32px'}} key={crypto}>
                                                                    <img className="radiocheck__payment-icon-img" 
                                                                    style={{width: '100%'}}
                                                                    src={`./img/crypto/${crypto}.svg`} alt={crypto} title={crypto.toUpperCase()} />
                                                                </span>
                                                            ))}
                                                        </span>
                                                    </span>
                                                </span>
                                            </label>*/}
                                            {errors.paymentMethod && (
                                                <div className="form__alert">{errors.paymentMethod.message}</div>
                                            )}
                                        </div>
                                    </>
                                }
                                <div className={`checkout__btns ${errors.root ? 'is-error' : ''}`}>
                                    <button
                                        className="btn btn_cta btn_lg btn_extrapad"
                                        type="submit"
                                        disabled={isSubmitting || !isCheckoutReady || Object.keys(errors).length > 0 || loadingStripe}
                                    >
                                        {isSubmitting || loadingStripe ? 'PROCESSING...' : 'PLACE ORDER'}
                                    </button>
                                    {errors.root && (
                                        <div className="form__alert form__alert_global">
                                            {errors.root.message}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </section>
        </>
    );
}

export default CheckoutPage;
